// Componente viewer del streaming en directo del juego VR del paciente.
//
// Renderiza <video> autoplay muted playsInline mas un panel de estado y
// metricas. NO contiene logica de signaling/WebRTC: esa esta en el hook
// useLiveStreamViewer.
//
// Se monta dentro de la tab "Sala" cuando se cumple TODO:
//   - La escena NO es de Mozilla Hubs/educaHub.
//   - Hay un code linkeado en estado "pending".
//   - El gameKey del code tiene supportsLiveStream=true.
//
// Sin estas condiciones la tab Sala mantiene su comportamiento legacy
// (iframe o Empty). Ver PageTherapistSesionInner.SessionVrCard.

import React from "react";
import { Alert, Card, Space, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useFormatos, SIN_DATO } from "../../../../i18n/formatos";
import {
  AppstoreOutlined,
  VideoCameraOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ApiOutlined,
} from "@ant-design/icons";
import { useLiveStreamViewer } from "../../../../hooks/revi/useLiveStreamViewer";
import "./LiveStreamViewer.scss";

const { Text } = Typography;

// Recibe `t` por parametro en vez de llamar al hook: no es un componente,
// es una funcion auxiliar, y los hooks solo pueden vivir dentro del render.
function statusTag(status, t) {
  switch (status) {
    case "requesting-turn":
      return (
        <Tag color="processing" icon={<ApiOutlined />}>
          {t("liveStream.status.requestingTurn")}
        </Tag>
      );
    case "waiting-peer":
      return (
        <Tag color="gold" icon={<ClockCircleOutlined spin />}>
          {t("liveStream.status.waitingPeer")}
        </Tag>
      );
    case "connecting":
      return (
        <Tag color="processing" icon={<ApiOutlined />}>
          {t("liveStream.status.connecting")}
        </Tag>
      );
    case "live":
      return (
        <Tag color="green" icon={<CheckCircleOutlined />}>
          {t("liveStream.status.live")}
        </Tag>
      );
    case "killed-by-fps":
      return (
        <Tag color="red" icon={<WarningOutlined />}>
          {t("liveStream.status.killedByFps")}
        </Tag>
      );
    case "peer-left":
      return (
        <Tag color="default" icon={<CloseCircleOutlined />}>
          {t("liveStream.status.peerLeft")}
        </Tag>
      );
    case "error":
      return (
        <Tag color="error" icon={<CloseCircleOutlined />}>
          {t("liveStream.status.error")}
        </Tag>
      );
    case "ended":
      return (
        <Tag color="default" icon={<CloseCircleOutlined />}>
          {t("liveStream.status.ended")}
        </Tag>
      );
    default:
      return <Tag>{t("liveStream.status.idle")}</Tag>;
  }
}

/**
 * bps / kbps / Mbps son simbolos tecnicos: no se traducen. Lo que SI cambia con
 * el idioma es el numero — el frances escribe 1,25 donde el ingles escribe
 * 1.25— asi que el valor pasa por el formateador y solo la unidad se pega.
 */
function formatBitrate(bps, numero) {
  if (bps === null || bps === undefined) return SIN_DATO;
  if (bps < 1000) return `${numero(bps)} bps`;
  if (bps < 1_000_000)
    return `${numero(bps / 1000, { maximumFractionDigits: 0 })} kbps`;
  return `${numero(bps / 1_000_000, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} Mbps`;
}

export function LiveStreamViewer({ accessToken, code, gameKey, gameLabel }) {
  const { t } = useTranslation("motricity");
  const { numero } = useFormatos();
  const { status, fps, rtt, bitrate, errorReason, killSwitchReason, videoRef } =
    useLiveStreamViewer({
      accessToken,
      code,
      gameKey,
      enabled: true,
    });

  const isVideoReady = status === "live" || status === "killed-by-fps";

  return (
    <Card
      className="live-stream-viewer"
      title={
        <Space>
          <VideoCameraOutlined />
          <span>{t("liveStream.title")}</span>
        </Space>
      }
      extra={statusTag(status, t)}
      variant="outlined"
    >
      <div className="live-stream-viewer__head">
        <Space size={12} wrap>
          <Text type="secondary">
            {t("liveStream.codeLabel")} <Text code>{code}</Text>
          </Text>
          {gameLabel ? (
            <Text type="secondary">
              <AppstoreOutlined /> {gameLabel}
            </Text>
          ) : null}
        </Space>
      </div>

      {status === "error" && errorReason ? (
        <Alert
          type="error"
          showIcon
          message={t("liveStream.alerts.connectFailed")}
          description={errorReason}
          style={{ marginBottom: 12 }}
        />
      ) : null}

      {status === "killed-by-fps" ? (
        <Alert
          type="warning"
          showIcon
          message={t("liveStream.alerts.pausedByGame")}
          // La causa es un codigo tecnico del juego ("fps-degraded"): es DATO
          // y entra sin traducir dentro de una frase que si es interfaz.
          description={t("liveStream.alerts.pausedByGameDetail", {
            causa: killSwitchReason || "fps-degraded",
          })}
          style={{ marginBottom: 12 }}
        />
      ) : null}

      <div className="live-stream-viewer__video-wrap">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          ref={videoRef}
          className="live-stream-viewer__video"
          autoPlay
          muted
          playsInline
          style={{
            visibility: isVideoReady ? "visible" : "hidden",
          }}
        />
        {!isVideoReady ? (
          <div className="live-stream-viewer__placeholder">
            {status === "waiting-peer" || status === "idle"
              ? t("liveStream.placeholder.waitingPeer")
              : status === "connecting"
                ? t("liveStream.placeholder.connecting")
                : status === "peer-left"
                  ? t("liveStream.placeholder.peerLeft")
                  : status === "ended"
                    ? t("liveStream.placeholder.ended")
                    : ""}
          </div>
        ) : null}
      </div>

      <div className="live-stream-viewer__metrics">
        <div>
          <Text type="secondary">{t("liveStream.metrics.fps")}</Text>
          <Text strong>{fps !== null ? fps : "—"}</Text>
        </div>
        <div>
          <Text type="secondary">{t("liveStream.metrics.rtt")}</Text>
          <Text strong>{rtt !== null ? `${numero(rtt)} ms` : SIN_DATO}</Text>
        </div>
        <div>
          <Text type="secondary">{t("liveStream.metrics.bitrate")}</Text>
          <Text strong>{formatBitrate(bitrate, numero)}</Text>
        </div>
      </div>
    </Card>
  );
}
